import React, { useState } from 'react'
import { XIcon } from 'lucide-react'
import { isInternalConversionCustomFieldKey } from '../../lib/leadConversionForm'
import { PAYMENT_METHODS, SALES_TAX_STATUSES } from '../property/types'
import type { CustomerManagementData, CustomerNote, UserProfile } from './types'

interface CustomerManagementFieldsProps {
  management: CustomerManagementData
  onManagementChange: (value: CustomerManagementData) => void
  customFields: Record<string, string>
  onCustomFieldsChange: (value: Record<string, string>) => void
  customerNotes: CustomerNote[]
  onCustomerNotesChange: (notes: CustomerNote[]) => void
  users: UserProfile[]
  showManagement?: boolean
  showCustomFields?: boolean
  showNotes?: boolean
  compact?: boolean
}

export function CustomerManagementFields({
  management,
  onManagementChange,
  customFields,
  onCustomFieldsChange,
  customerNotes,
  onCustomerNotesChange,
  users,
  showManagement = true,
  showCustomFields = true,
  showNotes = true,
  compact,
}: CustomerManagementFieldsProps) {
  const [notesDraft, setNotesDraft] = useState('')
  const [newCustomFieldKey, setNewCustomFieldKey] = useState('')
  const [newCustomFieldValue, setNewCustomFieldValue] = useState('')

  const visibleCustomFields = Object.fromEntries(
    Object.entries(customFields).filter(([key]) => !isInternalConversionCustomFieldKey(key))
  )

  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const addNote = () => {
    if (!notesDraft.trim()) return
    onCustomerNotesChange([
      { timestamp: new Date().toISOString(), note: notesDraft.trim() },
      ...customerNotes,
    ])
    setNotesDraft('')
  }

  const removeNote = (timestamp: string) => {
    onCustomerNotesChange(customerNotes.filter((n) => n.timestamp !== timestamp))
  }

  return (
    <div className="space-y-6">
      {showManagement && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Management</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={management.status}
                onChange={(e) => onManagementChange({ ...management, status: e.target.value })}
                className={inputClass}
              >
                <option value="onboarding">Onboarding</option>
                <option value="active">Active</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
              <select
                value={management.assigned_to}
                onChange={(e) => onManagementChange({ ...management, assigned_to: e.target.value })}
                className={inputClass}
              >
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Payment Method
              </label>
              <select
                value={management.payment_method}
                onChange={(e) => onManagementChange({ ...management, payment_method: e.target.value })}
                className={inputClass}
              >
                <option value="">Not set</option>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Sales Tax Status
              </label>
              <select
                value={management.sales_tax_status}
                onChange={(e) =>
                  onManagementChange({ ...management, sales_tax_status: e.target.value })
                }
                className={inputClass}
              >
                <option value="">Not set</option>
                {SALES_TAX_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Properties can use these defaults or override payment and tax settings per building.
          </p>
        </div>
      )}

      {showCustomFields && (
        <div className={showManagement ? 'pt-6 border-t border-gray-200' : ''}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Fields</h3>
          <div className="space-y-3">
            {Object.entries(visibleCustomFields).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{key}</label>
                  <input
                    type="text"
                    value={val}
                    onChange={(e) =>
                      onCustomFieldsChange({ ...customFields, [key]: e.target.value })
                    }
                    className={`${inputClass} text-sm`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...customFields }
                    delete next[key]
                    onCustomFieldsChange(next)
                  }}
                  className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove field"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-end gap-2 pt-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1">Field Name</label>
                <input
                  type="text"
                  value={newCustomFieldKey}
                  onChange={(e) => setNewCustomFieldKey(e.target.value)}
                  placeholder="e.g., Industry, Size, etc."
                  className={`${inputClass} text-sm`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1">Value</label>
                <input
                  type="text"
                  value={newCustomFieldValue}
                  onChange={(e) => setNewCustomFieldValue(e.target.value)}
                  placeholder="Field value"
                  className={`${inputClass} text-sm`}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (newCustomFieldKey.trim() && newCustomFieldValue.trim()) {
                    if (isInternalConversionCustomFieldKey(newCustomFieldKey.trim())) return
                    onCustomFieldsChange({
                      ...customFields,
                      [newCustomFieldKey.trim()]: newCustomFieldValue.trim(),
                    })
                    setNewCustomFieldKey('')
                    setNewCustomFieldValue('')
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotes && (
        <div className={showManagement || showCustomFields ? 'pt-6 border-t border-gray-200' : ''}>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Notes</h2>
          <div className="flex gap-2 mb-4">
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) addNote()
              }}
              rows={3}
              className={`flex-1 ${inputClass}`}
              placeholder="Add a note... (Ctrl+Enter to submit)"
            />
            <button
              type="button"
              onClick={addNote}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors self-start"
            >
              Add Note
            </button>
          </div>
          <div className="space-y-3">
            {customerNotes.length > 0 ? (
              customerNotes.map((note) => (
                <div key={note.timestamp} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{note.note}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(note.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeNote(note.timestamp)}
                    className="p-1 hover:bg-red-50 rounded transition-colors"
                  >
                    <XIcon className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">No notes yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
